import type { ObservableQuery } from '@apollo/client'
import { onlyCompleteData } from 'apollo-angular'
import type { OperatorFunction } from 'rxjs'
import { concatMap, of, pipe, throwError } from 'rxjs'

/**
 * Narrows a watch query to results that carry complete data, and reports failed
 * queries as an observable error.
 *
 * Apollo Client v4 `ObservableQuery`s never error. A failed query emits a
 * regular result that carries `error` and no data, and `onlyCompleteData()`
 * filters exactly those results out. Without rethrowing, a failing query would
 * therefore never notify its subscriber at all, which stalls every operator
 * that waits for an emission (`combineLatest`, `forkJoin`, `take(1)`, the
 * `async` pipe, ...). Rethrowing keeps `catchError` handlers in the UI
 * working.
 *
 * @returns An RxJS operator that emits complete query results and errors when
 *   the query fails.
 */
export function completeDataOrThrow<TData>(): OperatorFunction<
  ObservableQuery.Result<TData>,
  ObservableQuery.Result<TData, 'complete'>
> {
  return pipe(
    concatMap(result =>
      result.error ? throwError(() => result.error) : of(result)
    ),
    onlyCompleteData<TData>()
  )
}
